/**
 * Who may open a table, and whether AI may.
 *
 * The mirror of the column redaction dialog beside it -- a list of the
 * workspace's members with a switch each, written as a whole set -- and the
 * opposite question: redaction names the people a column is hidden *from*,
 * this names the people a table is open *to*.
 *
 * Two things the endpoint decides and this only reports. Admins are not on the
 * list and cannot be taken off it: they can open the table whatever it says.
 * And the list means nothing at all until the table is restricted, so it is
 * disabled rather than hidden while it is off -- somebody can see who would be
 * allowed before allowing anybody.
 *
 * `MCP access` is a separate axis rather than another member on the list: it
 * closes the channel, not the data, so a closed table stays open to every
 * person who could already open it. Closing the whole database to AI closes
 * this table too, whatever it says here.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { memberLabel, sortMembers } from '@/features/workspace/members'
import { useWorkspace } from '@/features/workspace/queries'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Label } from '@/shared/ui/label'
import { Skeleton } from '@/shared/ui/skeleton'
import { Switch } from '@/shared/ui/switch'
import { AccessSection, McpAccessSection } from '@/features/application/access/sections'
import { useSetTableAccess, useTableAccess } from './queries'

export function TableAccessDialog({
  workspaceId,
  tableId,
  tableName,
  onClose,
}: {
  workspaceId: number
  tableId: number
  tableName: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const workspace = useWorkspace(workspaceId)
  const access = useTableAccess(tableId)
  const save = useSetTableAccess(tableId)

  const [restricted, setRestricted] = useState<boolean | null>(null)
  const [mcpAccess, setMcpAccess] = useState(true)
  const [allowed, setAllowed] = useState<number[]>([])

  // Filled once the stored answer has arrived, and only then: starting from
  // "not restricted" and correcting it later would tell somebody the table is
  // open to everybody for as long as the request took, which is the opposite of
  // what they opened this to check.
  useEffect(() => {
    if (access.data === undefined || restricted !== null) return
    setRestricted(access.data.restrict_access)
    setMcpAccess(access.data.mcp_access)
    setAllowed(access.data.allowed_user_ids)
  }, [access.data, restricted])

  const members = sortMembers(workspace.data?.users ?? [])

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('access.title')}</DialogTitle>
          <DialogDescription>
            {t('access.description', { table: tableName })}
          </DialogDescription>
        </DialogHeader>

        {restricted === null ? (
          <Skeleton className="h-32" />
        ) : (
          <>
            <AccessSection title={t('memberAccess.title')}>
              <div className="flex items-center justify-between rounded border p-2">
                <Label htmlFor="access-restrict" className="text-sm font-normal">
                  {t('access.restrict')}
                </Label>
                <Switch
                  id="access-restrict"
                  checked={restricted}
                  onCheckedChange={setRestricted}
                />
              </div>

              <ul
                aria-label={t('access.allowed')}
                className="flex max-h-60 flex-col gap-2 overflow-y-auto"
              >
                {members.map((member) => (
                  <li key={member.user_id} className="flex items-center justify-between">
                    <Label
                      htmlFor={`access-${member.user_id}`}
                      className="text-sm font-normal"
                    >
                      {memberLabel(member)}
                    </Label>
                    <Switch
                      id={`access-${member.user_id}`}
                      disabled={!restricted}
                      checked={allowed.includes(member.user_id)}
                      onCheckedChange={(on) =>
                        setAllowed(
                          on
                            ? [...allowed, member.user_id]
                            : allowed.filter((id) => id !== member.user_id),
                        )
                      }
                    />
                  </li>
                ))}
              </ul>
            </AccessSection>

            <McpAccessSection
              id="access-mcp"
              description={t('access.mcpDescription')}
              checked={mcpAccess}
              onCheckedChange={setMcpAccess}
            />

            <DialogFooter>
              <Button variant="ghost" onClick={onClose}>
                {t('action.cancel')}
              </Button>
              <Button
                disabled={save.isPending}
                onClick={() => {
                  save.mutate(
                    {
                      restrict_access: restricted,
                      mcp_access: mcpAccess,
                      allowed_user_ids: allowed,
                    },
                    { onSuccess: onClose },
                  )
                }}
              >
                {t('action.save')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
